<template>
  <div class="card p-4">
    <div class="card-body d-flex flex-column align-items-center justify-content-center position-relative">
      <button class="back-btn" @click="$emit('back')" :disabled="disableBack">&#8592; Back</button>
      <div class="activate-title">Activate your new Taxi1 driver prepaid card</div>
      <img src="@/assets/images/driver/small-card.png" class="activate-card-img my-4" alt="card" />
      <div class="activate-desc mb-3">Please enter the EAID number found on the back of your new card. (case sensitive)</div>
      <input 
        type="text" 
        class="form-control activate-input mb-4" 
        placeholder="EAID number" 
        v-model="eaidNumber"
        :class="{ 'is-invalid': errors.eaid }"
        :disabled="isActivating"
      />
      <label class="pds-row" for="pds-agree">
        <span class="pds-check-label">
          <input
            id="pds-agree"
            v-model="pdsAccepted"
            type="checkbox"
            class="pds-check-input"
          />
          <span class="pds-check-box" aria-hidden="true" />
        </span>
        <span class="pds-text">
          <span class="pds-line">By activating my Taxi1 card, I confirm</span>
          <span class="pds-line">I have read and agree to the <a href="https://taxi1.com.au/wp-content/uploads/2026/04/PDS.pdf" class="pds-link" target="_blank" rel="noopener noreferrer" @click.stop>PDS</a></span>
        </span>
      </label>
      <button 
        class="btn btn-green activate-btn" 
        @click="handleActivate"
        :disabled="isActivating || !eaidNumber.trim() || !pdsAccepted"
      >
        {{ isActivating ? 'Activating...' : 'Activate' }}
      </button>
      
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useCardStore } from '@/store/driver/card'
import Swal from 'sweetalert2'

const props = defineProps({
  disableBack: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['back', 'success'])

const cardStore = useCardStore()
const eaidNumber = ref('')
const pdsAccepted = ref(false)
const isActivating = ref(false)
const errors = ref({
  eaid: ''
})

const validateForm = () => {
  errors.value.eaid = ''
  
  if (!eaidNumber.value.trim()) {
    errors.value.eaid = 'EAID number is required'
    return false
  }

  if (!pdsAccepted.value) {
    return false
  }

  return true
}

const handleActivate = async () => {
  if (!validateForm()) {
    return
  }
  
  isActivating.value = true
  
  try {
    // 串接 API
    await cardStore.activateCard(eaidNumber.value.trim())
    
    // 成功時導向卡片詳情頁面
    emit('success')
    
  } catch (error) {
    console.error('Card activation error:', error)
    
    // 處理錯誤訊息
    let errorMessage = 'Activation failed, please try again later'
    
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message
    } else if (error.message) {
      errorMessage = error.message
    }
    
    // 使用 SweetAlert 顯示錯誤訊息
    // Swal.fire({
    //   icon: 'error',
    //   title: errorMessage,
    //   confirmButtonText: 'OK'
    // })
    
  } finally {
    isActivating.value = false
  }
}
</script>

<style scoped>
.back-btn {
  position: absolute;
  top: 18px;
  right: 24px;
  background: #00c962;
  border: 1px solid #dbe2ea;
  border-radius: 20px;
  padding: 6px 18px;
  font-size: 1rem;
  color: #12223b;
  cursor: pointer;
  z-index: 2;
  transition: background 0.2s;
}
.back-btn:hover:not(:disabled) {
  background: #00c962;
  color: #fff;
}
.back-btn:disabled {
  background: #ccc;
  color: #666;
  cursor: not-allowed;
  opacity: 0.6;
}
.activate-title {
    font-family: 'Euclid Square';
    font-style: normal;
    font-weight: 500;
    font-size: 24px;
    line-height: 30px;
    text-align: center;
    color: #051C2C;
}
.activate-card-img {
  max-width: 300px;
  width: 100%;
  border-radius: 12px;
}
.activate-desc {
    font-family: 'Mulish';
    font-style: normal;
    font-weight: 400;
    font-size: 18px;
    line-height: 23px;

    color: #051C2C;
}
.activate-input {
  max-width: 320px;
  width: 100%;
  border-radius: 8px;
  border: 1px solid #dbe2ea;
  padding: 10px 16px;
  font-size: 1rem;
  text-align: center;
}
.activate-btn {
  background: #00c962;
  color: #051C2C;
  border-radius: 24px;
  padding: 10px 32px;
  font-size: 1rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  min-width: 180px;
}
.activate-btn:hover {
  background: #00c962;
  color: #fff;
}

.activate-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.error-message {
  color: #ff4040;
  font-size: 0.875rem;
  margin-top: 4px;
  margin-bottom: 8px;
  text-align: center;
}

.is-invalid {
  border-color: #ff4040 !important;
}
.pds-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  max-width: 360px;
  width: 100%;
  margin-bottom: 1.5rem;
  cursor: pointer;
}

.pds-check-label {
  position: relative;
  flex-shrink: 0;
  margin: 2px 0 0 0;
}

.pds-check-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  margin: 0;
}

.pds-check-box {
  display: block;
  position: relative;
  width: 22px;
  height: 22px;
  border: 2px solid #c5ced6;
  border-radius: 6px;
  background: #fff;
  box-sizing: border-box;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.pds-check-input:focus-visible + .pds-check-box {
  outline: 2px solid #00c962;
  outline-offset: 2px;
}

.pds-check-input:checked + .pds-check-box {
  background: #00c962;
  border-color: #00c962;
}

.pds-check-input:checked + .pds-check-box::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 5px;
  height: 10px;
  margin-left: -3px;
  margin-top: -7px;
  border: solid #051c2c;
  border-width: 0 2.5px 2.5px 0;
  transform: rotate(45deg);
}

.pds-text {
  font-family: 'Mulish', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 23px;
  color: #051c2c;
  flex: 1;
  min-width: 0;
}

.pds-line {
  display: block;
}
.pds-link {
  color: #051c2c;
  text-decoration: underline;
  font-weight: 600;
}
</style> 